// src/pages/AnalysisDashboard.jsx
import React, { useState, useRef } from "react";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import { jsPDF } from "jspdf";
import "./AnalysisDashboard.css";

// ── Fetch recent games directly from Chess.com / Lichess ──────
// Returns a normalized list: [{ id, white, black, result, timeControl,
// timeClass, date, pgn }]. Each game's pgn includes clock times.

function classifyTimeControl(tc) {
  // tc like "600", "600+5", "180", "60+0" -> rapid/blitz/bullet/classical
  const base = parseInt(String(tc).split("+")[0], 10) || 0;
  if (base >= 1500) return "classical";
  if (base >= 480) return "rapid";
  if (base >= 180) return "blitz";
  return "bullet";
}

// Given a normalized game and the searched username, work out which side the
// player was, their rating in that game, and the W/L/D from their view.
function playerView(white, black, whiteElo, blackElo, result, username) {
  const isWhite = (white || "").toLowerCase() === (username || "").toLowerCase();
  const playerElo = isWhite ? whiteElo : blackElo;
  let outcome = "draw";
  if (result === "1-0") outcome = isWhite ? "win" : "loss";
  else if (result === "0-1") outcome = isWhite ? "loss" : "win";
  return { isWhite, playerElo: playerElo || null, outcome };
}

async function fetchLichessGames(username, max = 10) {
  // Lichess gives PGN with clocks in one request. No key needed.
  const url = `https://lichess.org/api/games/user/${encodeURIComponent(username)}?max=${max}&clocks=true&opening=true&perfType=ultraBullet,bullet,blitz,rapid,classical`;
  const res = await fetch(url, { headers: { Accept: "application/x-chess-pgn" } });
  if (!res.ok) {
    if (res.status === 404) throw new Error(`Lichess user "${username}" not found.`);
    throw new Error(`Lichess request failed (${res.status}).`);
  }
  const text = await res.text();
  if (!text.trim()) throw new Error("No recent games found for that Lichess user.");
  // Split into individual games (each starts with [Event)
  const blocks = text.trim().split(/(?=\[Event\s)/).map(b => b.trim()).filter(Boolean);
  return blocks.map((block, i) => {
    const h = {};
    const re = /\[(\w+)\s+"([^"]*)"\]/g; let m;
    while ((m = re.exec(block)) !== null) h[m[1]] = m[2];
    const tc = h.TimeControl || "";
    const whiteElo = parseInt(h.WhiteElo, 10) || null;
    const blackElo = parseInt(h.BlackElo, 10) || null;
    const result = h.Result || "*";
    const pv = playerView(h.White, h.Black, whiteElo, blackElo, result, username);
    return {
      id: `li_${i}`,
      white: h.White || "?", black: h.Black || "?",
      result, timeControl: tc,
      timeClass: classifyTimeControl(tc),
      date: h.UTCDate || h.Date || "",
      playerElo: pv.playerElo, outcome: pv.outcome,
      pgn: block,
    };
  });
}

async function fetchChessComGames(username, max = 10) {
  // Chess.com organizes games into monthly archives. Pull the most recent
  // month(s) until we have enough games. Games include clocks in the PGN.
  const uname = username.toLowerCase();
  const archRes = await fetch(`https://api.chess.com/pub/player/${encodeURIComponent(uname)}/games/archives`);
  if (!archRes.ok) {
    if (archRes.status === 404) throw new Error(`Chess.com user "${username}" not found.`);
    throw new Error(`Chess.com request failed (${archRes.status}).`);
  }
  const { archives } = await archRes.json();
  if (!archives || archives.length === 0) throw new Error("No games found for that Chess.com user.");

  const collected = [];
  // Walk newest archive backwards until we have `max` games (cap at 3 months)
  for (let a = archives.length - 1; a >= 0 && collected.length < max && a >= archives.length - 3; a--) {
    const mRes = await fetch(archives[a]);
    if (!mRes.ok) continue;
    const { games } = await mRes.json();
    if (!games) continue;
    // newest last in the month; iterate from the end
    for (let g = games.length - 1; g >= 0 && collected.length < max; g--) {
      const game = games[g];
      if (!game.pgn) continue;
      const tc = game.time_control || "";
      const result = game.white?.result === "win" ? "1-0" : game.black?.result === "win" ? "0-1" : "1/2-1/2";
      const whiteElo = game.white?.rating || null;
      const blackElo = game.black?.rating || null;
      const pv = playerView(game.white?.username, game.black?.username, whiteElo, blackElo, result, uname);
      collected.push({
        id: `cc_${a}_${g}`,
        white: game.white?.username || "?",
        black: game.black?.username || "?",
        result,
        timeControl: tc,
        timeClass: game.time_class || classifyTimeControl(tc),
        date: game.end_time ? new Date(game.end_time * 1000).toISOString().slice(0, 10) : "",
        playerElo: pv.playerElo, outcome: pv.outcome,
        pgn: game.pgn,
      });
    }
  }
  if (collected.length === 0) throw new Error("No recent games found for that Chess.com user.");
  return collected;
}

function fetchRecentGames(site, username, max = 10) {
  return site === "lichess"
    ? fetchLichessGames(username, max)
    : fetchChessComGames(username, max);
}

// ── Gemini API (direct, admin-only use) ──────────────────────
// Pass { json: true } to force strict JSON output. In JSON mode Gemini is
// constrained to emit valid JSON only — no markdown fences, no stray prose,
// and no unescaped-quote corruption — which is what was breaking the
// curriculum parse.
async function callGemini(prompt, opts = {}) {
  try {
    const apiKey = process.env.REACT_APP_GEMINI_KEY;
    if (!apiKey) { console.warn("No Gemini key found in .env"); return ""; }
    const model = window._geminiModel || "gemini-3.5-flash";
    const generationConfig = { temperature: 0.7, maxOutputTokens: opts.maxTokens || 3000 };
    if (opts.json) generationConfig.responseMimeType = "application/json";
    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig
        })
      }
    );
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data.candidates?.[0]?.content?.parts?.[0]?.text || "";
  } catch (err) {
    console.error("Gemini error:", err);
    return "";
  }
}


// ── Parse FEN into human-readable board description ──────────────────────────
function fenToDescription(fen) {
  try {
    const pieceNames = {
      'K': 'White King', 'Q': 'White Queen', 'R': 'White Rook',
      'B': 'White Bishop', 'N': 'White Knight', 'P': 'White Pawn',
      'k': 'Black King', 'q': 'Black Queen', 'r': 'Black Rook',
      'b': 'Black Bishop', 'n': 'Black Knight', 'p': 'Black Pawn'
    };
    const files = ['a','b','c','d','e','f','g','h'];
    const rows = fen.split(' ')[0].split('/');
    const pieces = [];
    rows.forEach((row, rankIdx) => {
      const rank = 8 - rankIdx;
      let fileIdx = 0;
      for (const ch of row) {
        if (isNaN(ch)) {
          pieces.push(pieceNames[ch] + ' on ' + files[fileIdx] + rank);
          fileIdx++;
        } else {
          fileIdx += parseInt(ch);
        }
      }
    });
    const turn = fen.split(' ')[1] === 'w' ? 'White' : 'Black';
    return turn + ' to move. Pieces: ' + pieces.join(', ') + '.';
  } catch { return 'Position: ' + fen; }
}

// ── Get legal moves for a square in human-readable form ──────────────────────
function getLegalMovesText(fen, square) {
  try {
    const g = new Chess(fen);
    const moves = g.moves({ square, verbose: true });
    if (moves.length === 0) return 'no legal moves';
    return moves.map(m => m.san).join(', ');
  } catch { return ''; }
}

// ── Get tactical follow-up moves after bestMove ──────────────────────────────
async function getTacticalFollowUp(fen, bestMoveUci, playerElo) {
  try {
    if (!bestMoveUci) return null;
    const rawBm = bestMoveUci.trim().split(/\s+/).find(p => p.length >= 4 && /^[a-h][1-8][a-h][1-8]/.test(p));
    if (!rawBm) return null;

    // Build the tactical line — up to 6 moves deep
    const followUpMoves = [];
    let currentFen = fen;
    let currentGame = new Chess(fen);

    // First move: the better move we should have played
    const firstMove = currentGame.move({ from: rawBm.slice(0,2), to: rawBm.slice(2,4), promotion: 'q' });
    if (!firstMove) return null;
    followUpMoves.push({ from: rawBm.slice(0,2), to: rawBm.slice(2,4), san: firstMove.san, fenBefore: currentFen });
    currentFen = currentGame.fen();

    // Continue with engine moves for up to 5 more plies
    for (let i = 0; i < 5; i++) {
      const resp = await analyzePosition(currentFen, 12);
      if (!resp.bestmove) break;
      const bm = resp.bestmove.trim().split(/\s+/).find(p => p.length >= 4 && /^[a-h][1-8][a-h][1-8]/.test(p));
      if (!bm) break;
      const g = new Chess(currentFen);
      const mv = g.move({ from: bm.slice(0,2), to: bm.slice(2,4), promotion: 'q' });
      if (!mv) break;
      followUpMoves.push({ from: bm.slice(0,2), to: bm.slice(2,4), san: mv.san, fenBefore: currentFen });
      currentFen = g.fen();
      // Stop if game is over
      if (g.isGameOver()) break;
      await new Promise(r => setTimeout(r, 1100));
    }

    // Evaluate the final position
    const finalEval = await analyzePositionRetry(currentFen, 14);
    const finalEvalPawns = finalEval.evaluation ?? 0; // default to "equal" if the call failed

    // Ask Gemini to explain:
    // 1. What the tactic is
    // 2. Why the resulting position is better (in human terms)
    const boardDesc = fenToDescription(fen);
    const finalBoardDesc = fenToDescription(currentFen);
    const moveLine = followUpMoves.map((m, i) => (i % 2 === 0 ? "★" : "→") + " " + m.san).join(" ");
    const evalDesc = finalEvalPawns > 2 ? "clearly winning" :
                     finalEvalPawns > 0.5 ? "better" :
                     finalEvalPawns > 0 ? "slightly better" :
                     finalEvalPawns > -0.5 ? "roughly equal" : "worse";

    const tacticalPrompt =
      "You are a chess coach explaining to a " + playerElo + " ELO player why a tactical line is better.\n\n" +
      "STARTING POSITION: " + boardDesc + "\n\n" +
      "BEST LINE: " + moveLine + "\n\n" +
      "RESULTING POSITION: " + finalBoardDesc + "\n" +
      "After this line, the position is " + evalDesc + ".\n\n" +
      "Write exactly 3 complete sentences:\n" +
      "Sentence 1: What specific tactic or concept makes the first move (" + followUpMoves[0].san + ") strong?\n" +
      "Sentence 2: What happens in the resulting position? Mention material gains OR positional advantages like: king safety, central control, active pieces, passed pawns, rook on open file, attacking chances.\n" +
      "Sentence 3: One concrete tip — what pattern should the player remember for similar positions?\n\n" +
      "Be specific about pieces and squares. Do not mention evaluation numbers. Start directly without introduction.";

    let explanation = await callGemini(tacticalPrompt);

    // If empty or too generic, try once more with a simpler prompt
    if (!explanation || explanation.length < 30 || explanation.toLowerCase().includes("significantly better")) {
      const retryPrompt =
        "Chess position after the line: " + moveLine + "\n" +
        "Final position: " + finalBoardDesc + "\n" +
        "The position is " + evalDesc + " for the player who started this line.\n\n" +
        "Name ONE specific advantage gained (e.g. 'wins the rook on d8', 'bishop pins the queen', " +
        "'passed pawn on e6 will queen', 'king is exposed to attack on g1', " +
        "'rook dominates the 7th rank', 'knight has an outpost on d5').\n" +
        "Write ONE sentence only. Be concrete and specific.";
      explanation = await callGemini(retryPrompt);
    }

    return {
      moves: followUpMoves,
      finalFen: currentFen,
      explanation: explanation || null,
    };
  } catch(e) {
    console.error("Tactical follow-up error:", e);
    return null;
  }
}


// ── Stockfish API ─────────────────────────────────────────────
async function analyzePosition(fen, depth = 12) {
  try {
    const res = await fetch(
      `https://stockfish.online/api/s/v2.php?fen=${encodeURIComponent(fen)}&depth=${depth}`
    );
    const data = await res.json();
    // IMPORTANT: a failed/throttled call must NOT look like "0.0 = equal".
    // Counting a failure as eval 0 makes a move score ~100% accuracy and
    // silently inflates results. Return evaluation: null so callers can skip it.
    if (!data.success || typeof data.evaluation !== "number") {
      return { evaluation: null, bestmove: data?.bestmove || null, mate: data?.mate ?? null, ok: false };
    }
    return {
      evaluation: data.evaluation,
      bestmove: data.bestmove || null,
      mate: data.mate || null,
      ok: true,
    };
  } catch { return { evaluation: null, bestmove: null, mate: null, ok: false }; }
}

// Retry a couple of times on failure — the public API throttles under load.
async function analyzePositionRetry(fen, depth = 12, tries = 3) {
  for (let t = 0; t < tries; t++) {
    const r = await analyzePosition(fen, depth);
    if (r.ok) return r;
    await new Promise(res => setTimeout(res, 600 * (t + 1))); // back off
  }
  return { evaluation: null, bestmove: null, mate: null, ok: false };
}

// ── SAN from UCI ──────────────────────────────────────────────
// Converts "e2e4" → "e4", "g1f3" → "Nf3", etc.
function uciToSan(fen, uci) {
  if (!uci || uci.length < 4) return uci;
  try {
    const chess = new Chess(fen);
    const from = uci.slice(0, 2);
    const to   = uci.slice(2, 4);
    const promo = uci[4] ? uci[4].toLowerCase() : undefined;
    const move = chess.move({ from, to, promotion: promo });
    if (move) return move.san;
    // Fallback: try all legal moves to find one matching from/to
    const legal = chess.moves({ verbose: true });
    const match = legal.find(m => m.from === from && m.to === to);
    return match ? match.san : uci;
  } catch { return uci; }
}

// ── Accuracy ──────────────────────────────────────────────────
function evalToWinProb(cp) {
  return 1 / (1 + Math.exp(-0.00368208 * cp));
}
function moveAccuracy(cpBefore, cpAfter) {
  const drop = Math.max(0, evalToWinProb(cpBefore) - evalToWinProb(cpAfter));
  return Math.max(0, Math.min(100, 103.1668 * Math.exp(-0.04354 * (drop * 100)) - 3.1669));
}

// ── Classify mistake properly ─────────────────────────────────
// A blunder = goes from winning/equal to losing
// A mistake = significant loss but not game-changing
// An inaccuracy = small suboptimal move
function classifyByPosition(evalBeforeCp, evalAfterCp, drop, thresholds) {
  // Positions from perspective of moving player (positive = they're winning)
  const wasWinning = evalBeforeCp > 100;   // was winning
  const wasEqual   = Math.abs(evalBeforeCp) <= 100; // was roughly equal
  const nowLosing  = evalAfterCp < -100;   // now losing
  const nowWorse   = evalAfterCp < evalBeforeCp - 100; // significantly worse

  if ((wasWinning || wasEqual) && nowLosing && drop >= 200) return "blunder";
  if (drop >= thresholds.blunder) return "blunder";
  if (drop >= thresholds.mistake) return "mistake";
  return "inaccuracy";
}

// ── Full Analysis ─────────────────────────────────────────────
async function fullAnalyze(games, playerElo, playerUsername, onProgress) {
  const tagged = [];
  const thresholds = getThresholds(playerElo);
  const totalMoves = games.reduce((s, g) => s + g.moves.length, 0);
  let done = 0;
  const accuracies = [];

  for (let gi = 0; gi < games.length; gi++) {
    const game = games[gi];

    // Determine player color
    const playerColor = playerUsername
      ? (game.white.toLowerCase().includes(playerUsername.toLowerCase()) ? "w" : "b")
      : "w"; // default white if no username given
    game.playerColor = playerColor;

    const chess = new Chess();

    for (let i = 0; i < game.moves.length; i++) {
      const move = game.moves[i];
      const fenBefore = chess.fen();
      const moveNum = Math.ceil((i + 1) / 2);
      const isPlayerMove = move.color === playerColor;

      const before = await analyzePositionRetry(fenBefore, 12);
      chess.move(move);
      const fenAfter = chess.fen();
      const after = await analyzePositionRetry(fenAfter, 12);

      // If either evaluation failed, SKIP this move entirely — don't let a
      // failed engine call masquerade as a perfect (100%) move.
      if (before.evaluation === null || after.evaluation === null) {
        done++;
        if (onProgress) onProgress(Math.round((done / totalMoves) * 85));
        await new Promise(r => setTimeout(r, 1300));
        continue;
      }

      // From moving player's perspective
      const evalBeforeCp = move.color === "w" ?  before.evaluation * 100 : -before.evaluation * 100;
      const evalAfterCp  = move.color === "w" ?  after.evaluation  * 100 : -after.evaluation  * 100;
      const drop = evalBeforeCp - evalAfterCp;

      if (isPlayerMove) {
        accuracies.push(moveAccuracy(evalBeforeCp, evalAfterCp));
      }

      done++;
      if (onProgress) onProgress(Math.round((done / totalMoves) * 85));

      // Only tag player's mistakes
      if (!isPlayerMove || drop < thresholds.inaccuracy) {
        await new Promise(r => setTimeout(r, 1300)); // rate limit buffer for Stockfish + Gemini
        continue;
      }

      // Get best move in UCI
      // Stockfish returns: "bestmove e2e4 ponder e7e5" — need to parse carefully
      let bestUci = null, bestFrom = null, bestTo = null, bestSan = null;
      if (before.bestmove) {
        const raw = before.bestmove.trim();
        const parts = raw.split(/\s+/);
        // Find the move token: 4 chars like "e2e4" or 5 like "e7e8q"
        const bm = parts.find(p =>
          p.length >= 4 && p.length <= 5 &&
          p !== "bestmove" && p !== "ponder" && p !== "(none)" &&
          /^[a-h][1-8][a-h][1-8]/.test(p)
        );
        if (bm) {
          bestUci  = bm;
          bestFrom = bm.slice(0, 2);
          bestTo   = bm.slice(2, 4);
          bestSan  = uciToSan(fenBefore, bm);
          console.log("[BestMove] parsed:", bm, "→", bestSan);
        } else {
          console.warn("[BestMove] could not parse:", raw);
        }
      }

      // Verify best move isn't the same as played move
      const playedUci = `${move.from}${move.to}`;
      if (bestUci && bestUci.slice(0,4) === playedUci.slice(0,4)) {
        // Engine agrees with move — skip
        await new Promise(r => setTimeout(r, 1300)); // rate limit buffer for Stockfish + Gemini
        continue;
      }

      const severityType = classifyByPosition(evalBeforeCp, evalAfterCp, drop, thresholds);
      const pawnsLost = (drop / 100).toFixed(1);

      // Use fast rule-based tagging during analysis (saves Gemini tokens)
      // Gemini tagging happens lazily when user expands a weakness card
      const aiTag = quickWeaknessTag(move, moveNum, severityType, Math.round(evalBeforeCp), Math.round(evalAfterCp), bestSan);

      tagged.push({
        tag: aiTag,
        label: aiTag, // AI already gives human-readable label
        severity: drop,
        severityType,
        pawnsLost,
        gameIndex: gi,
        moveNumber: moveNum,
        san: move.san,
        from: move.from,
        to: move.to,
        fen: fenBefore,
        fenAfter,
        evalBeforeCp: Math.round(evalBeforeCp),
        evalAfterCp: Math.round(evalAfterCp),
        bestFrom,
        bestTo,
        bestSan,
        bestUci,
        moveColor: move.color,
        piece: move.piece,
        explanation: null,
      });

      await new Promise(r => setTimeout(r, 1300)); // rate limit buffer for Stockfish + Gemini
    }
  }

  const overallAccuracy = accuracies.length > 0
    ? Math.round(accuracies.reduce((s, a) => s + a, 0) / accuracies.length)
    : 0;

  return { tagged, overallAccuracy, accuracyMoveCount: accuracies.length };
}

function getThresholds(elo) {
  if (elo < 800)  return { blunder: 200, mistake: 100, inaccuracy: 50 };
  if (elo < 1000) return { blunder: 180, mistake: 90,  inaccuracy: 45 };
  if (elo < 1200) return { blunder: 150, mistake: 80,  inaccuracy: 40 };
  if (elo < 1500) return { blunder: 130, mistake: 70,  inaccuracy: 35 };
  return            { blunder: 100, mistake: 60,  inaccuracy: 30 };
}

// AI analyses each mistake and returns a chess concept label
// ── Fast rule-based tagger (no API call) ────────────────────
// Used during analysis to save Gemini tokens
// AI tagging only happens when user clicks to expand
function quickWeaknessTag(move, moveNum, severityType, evalBeforeCp, evalAfterCp, bestSan) {
  if (moveNum <= 15) return "Opening Mistake";
  if (move.piece === "k") return "King Safety";
  if (move.piece === "p" && moveNum <= 20) return "Pawn Weakness";
  if (severityType === "blunder" && evalBeforeCp > 50 && evalAfterCp < -50) return "Major Blunder";
  if (severityType === "blunder") return "Tactical Miscalculation";
  if (severityType === "mistake" && move.piece === "r") return "Rook Activation";
  if (severityType === "mistake") return "Missed Opportunity";
  return "Inaccuracy";
}

async function getAIWeaknessTag(fen, moveSan, bestSan, evalBeforeCp, evalAfterCp, severityType, moveNum, playerElo) {
  // Convert board to English so Gemini understands the actual position
  const boardDesc = fenToDescription(fen);
  const evalBefore = evalBeforeCp >= 0 ? "+" + (evalBeforeCp/100).toFixed(1) : (evalBeforeCp/100).toFixed(1);
  const evalAfter = evalAfterCp >= 0 ? "+" + (evalAfterCp/100).toFixed(1) : (evalAfterCp/100).toFixed(1);

  const prompt =
    "You are analyzing a chess position. A " + severityType + " was made on move " + moveNum + ".\n\n" +
    "EXACT BOARD: " + boardDesc + "\n\n" +
    "Move played: " + moveSan + " (eval: " + evalBefore + " → " + evalAfter + ")\n" +
    "Better move: " + (bestSan || "unknown") + "\n\n" +
    "Based on the ACTUAL pieces in this position, identify the ONE most accurate chess concept.\n" +
    "Only choose from this list — pick whichever best describes what actually happened:\n" +
    "Missed Pin, Missed Skewer, Missed Fork, Missed Discovered Attack, Back Rank Weakness, " +
    "Missed Checkmate, Unsafe King, 7th Rank Control, Hanging Piece, Opening Mistake, " +
    "Pawn Weakness, Piece Activity, Endgame Technique, Tactical Miscalculation, " +
    "Weak Square, Rook Activation, King Safety\n\n" +
    "IMPORTANT: Only pick Missed Checkmate if there is actually a checkmate available. " +
    "Only pick Missed Fork if a piece can actually attack two enemy pieces simultaneously. " +
    "Only pick Missed Pin if a piece is actually pinned to a valuable piece behind it.\n\n" +
    "Reply with ONLY the concept name from the list. Nothing else.";

  const result = await callGemini(prompt);
  const clean = (result || "").trim().replace(/[^a-zA-Z0-9 ]/g, "").trim();
  // Validate it's actually from our list
  const validTags = ["Missed Pin","Missed Skewer","Missed Fork","Missed Discovered Attack",
    "Back Rank Weakness","Missed Checkmate","Unsafe King","7th Rank Control","Hanging Piece",
    "Opening Mistake","Pawn Weakness","Piece Activity","Endgame Technique",
    "Tactical Miscalculation","Weak Square","Rook Activation","King Safety"];
  const match = validTags.find(t => clean.toLowerCase().includes(t.toLowerCase()));
  return match || "Tactical Miscalculation";
}


async function getMoveExplanation(example, playerElo) {
  // Convert FEN to human-readable description so Gemini doesn't hallucinate
  const boardDesc = fenToDescription(example.fen);
  const evalBefore = example.evalBeforeCp >= 0
    ? "winning (+" + (example.evalBeforeCp/100).toFixed(1) + " pawns)"
    : "losing (" + (example.evalBeforeCp/100).toFixed(1) + " pawns)";
  const evalAfter = example.evalAfterCp >= 0
    ? "still winning (+" + (example.evalAfterCp/100).toFixed(1) + " pawns)"
    : "now losing (" + (example.evalAfterCp/100).toFixed(1) + " pawns)";

  const prompt =
    "You are a chess coach. A " + (playerElo || 1000) + " ELO player made a " + example.severityType + " on move " + example.moveNumber + ".\n\n" +
    "EXACT BOARD POSITION: " + boardDesc + "\n\n" +
    "Move played: " + example.san + " — position was " + evalBefore + ", after this move it became " + evalAfter + ".\n" +
    "Better move was: " + (example.bestSan || "a different move") + "\n\n" +
    "CRITICAL RULES:\n" +
    "- Only mention pieces that ACTUALLY EXIST in the position listed above\n" +
    "- Do not invent pieces, pawns, or threats that are not in the position\n" +
    "- Be specific: name exact squares and piece types\n\n" +
    "Write exactly 2 complete sentences:\n" +
    "Sentence 1: Why " + example.san + " was the wrong move, referencing the actual pieces and squares in the position.\n" +
    "Sentence 2: Why " + (example.bestSan || "the better move") + " is stronger, what it specifically achieves in this position.\n\n" +
    "End both sentences with a period. Start directly without introduction.";

  const result = await callGemini(prompt);
  if (!result || result.length < 15) return null; // return null so retry shows

  const trimmed = result.trim();
  const lastPeriod = Math.max(trimmed.lastIndexOf("."), trimmed.lastIndexOf("!"), trimmed.lastIndexOf("?"));
  return lastPeriod > trimmed.length * 0.4 ? trimmed.slice(0, lastPeriod + 1) : trimmed;
}



async function generateCurriculum(weaknesses, playerElo) {
  // Generate curriculum even with just 1-2 weaknesses found
  const topWeaknesses = (weaknesses || []).slice(0, 8);

  // If no weaknesses detected, create a general beginner curriculum
  const weaknessSummary = topWeaknesses.length > 0
    ? topWeaknesses.map(w => "- " + w.label + ": found " + w.frequency + " time(s), avg " + w.avgPawnsLost + " pawns lost per mistake").join("\n")
    : "- No specific weaknesses detected yet (need more games for detailed analysis)";

  const prompt =
    "You are a professional chess coach. Build a 6-8 week training curriculum for a " + playerElo + " ELO player targeting " + (playerElo + 200) + " ELO.\n\n" +
    "Analysis of their games found:\n" + weaknessSummary + "\n\n" +
    "CREATE THE CURRICULUM NOW. Do not say you need more data.\n\n" +
    "RULES FOR TOPICS — must be specific chess concepts like these examples:\n" +
    "✓ GOOD: 'Back Rank Checkmate Patterns', 'Knight Fork on f7 and Similar Motifs', " +
    "'Rook to the 7th Rank Technique', 'King Safety — Recognizing Kingside Attacks', " +
    "'Open File Control with Rooks', 'Passed Pawn Advancement', 'Bishop vs Knight Endgames', " +
    "'Sicilian Defense — Basic Ideas', 'Piece Coordination and Outpost Squares'\n" +
    "✗ BAD (never use): 'Improve tactics', 'Work on blunders', 'Be more careful', 'Practice openings'\n\n" +
    "RETURN ONLY a valid JSON array. No text before [. No text after ]. No markdown fences.\n" +
    "Each item must have exactly these keys: week, phase, topic, explanation, goal, dailyExercises (array of 3), resources (array of 2)\n\n" +
    "[{" +
    '"week":1,' +
    '"phase":"Plugging Leaks",' +
    '"topic":"Back Rank Checkmate — Recognition and Prevention",' +
    '"explanation":"At ' + playerElo + ' ELO the back rank is the most common source of decisive blunders",' +
    '"goal":"Spot all back rank threats before making your move in your next 5 games",' +
    '"dailyExercises":["Solve 20 back rank puzzles on Lichess (filter: Back Rank Mate theme)","Before every rook/queen move ask: does this allow a back rank mate?","Review one of your lost games specifically looking for back rank threats"],' +
    '"resources":["Lichess Puzzles — Back Rank Mate theme (free at lichess.org/training)","GothamChess YouTube — Checkmate Patterns Every Player Must Know"]' +
    "},...]";

  console.log("[Curriculum] Calling Gemini...");
  // High token budget — a multi-week curriculum is long, and a truncated
  // response is invalid JSON. JSON mode keeps it well-formed; this keeps it
  // from being cut off mid-array.
  const text = await callGemini(prompt, { json: true, maxTokens: 8000 });
  console.log("[Curriculum] Response length:", text?.length, "Preview:", text?.slice(0, 150));

  if (!text || text.length < 20) {
    console.error("[Curriculum] Empty response from Gemini");
    return [];
  }

  try {
    // In JSON mode the response is already clean, but stay defensive:
    // strip any stray code fences, then slice to the outermost [ ... ].
    let clean = text.trim().replace(/^```(?:json)?/i, "").replace(/```$/, "").trim();
    const startIdx = clean.indexOf("[");
    const endIdx = clean.lastIndexOf("]");
    if (startIdx === -1 || endIdx <= startIdx) {
      console.error("[Curriculum] No JSON array in response:", clean.slice(0, 200));
      return [];
    }
    const parsed = JSON.parse(clean.slice(startIdx, endIdx + 1));
    console.log("[Curriculum] Success:", parsed.length, "weeks");
    return Array.isArray(parsed) ? parsed : [];
  } catch(e) {
    console.error("[Curriculum] Parse error:", e.message, "\nRaw:", text?.slice(0, 300));
    return [];
  }
}


// ── PGN Parser ────────────────────────────────────────────────
// Handles both Chess.com and Lichess exports, single or many games per paste.
// Games are split on each "[Event" tag (the one reliable game boundary across
// sites), not on blank lines — Lichess puts all moves on one line with no
// trailing blank, which broke the old blank-line split.

// chess.js can reject a game if the movetext has comments it doesn't like
// (Lichess injects { opening name } and { White wins... } blocks). We strip
// comments and result/NAG noise for PARSING only — the raw block is kept
// separately so [%clk] times are still read from the untouched text.
function sanitizeMovetext(block) {
  // Split headers from movetext: movetext starts after the last header line.
  const headerRe = /^\[.*\]\s*$/gm;
  let lastHeaderEnd = 0;
  let m;
  while ((m = headerRe.exec(block)) !== null) lastHeaderEnd = m.index + m[0].length;
  const headers = block.slice(0, lastHeaderEnd);
  let moves = block.slice(lastHeaderEnd);

  moves = moves
    .replace(/\{[^}]*\}/g, " ")   // remove all { ... } comments (clocks, openings, results)
    .replace(/\$\d+/g, " ")       // remove NAG annotations like $1
    .replace(/\([^()]*\)/g, " ")  // remove variation lines in ( )
    .replace(/\s+/g, " ")         // collapse whitespace
    .trim();

  return headers + "\n\n" + moves;
}

function parsePGNGames(text) {
  const clean = text.trim();
  if (!clean) return [];

  // Split into per-game blocks at each [Event tag. The lookahead keeps the
  // [Event with its game. Works for one game or many in a single paste.
  let blocks = clean.split(/(?=\[Event\s)/).map(b => b.trim()).filter(Boolean);
  // Fallback: if there were no [Event tags at all, treat the whole paste as one game.
  if (blocks.length === 0) blocks = [clean];

  return blocks.map(block => {
    const headers = {};
    const re = /\[(\w+)\s+"([^"]*)"\]/g;
    let m;
    while ((m = re.exec(block)) !== null) headers[m[1]] = m[2];

    const chess = new Chess();
    // Try the original block first; if chess.js rejects it (Lichess comments),
    // retry with sanitized movetext. Either way clocks come from `raw` below.
    let loaded = false;
    try { chess.loadPgn(block); loaded = chess.history().length > 0; } catch {}
    if (!loaded) {
      try { chess.loadPgn(sanitizeMovetext(block)); loaded = chess.history().length > 0; } catch {}
    }

    return {
      headers,
      white: headers.White || "?",
      black: headers.Black || "?",
      result: headers.Result || "*",
      opening: headers.Opening || headers.ECO || "Unknown Opening",
      date: headers.Date || "",
      timeControl: headers.TimeControl || "",
      moves: chess.history({ verbose: true }),
      raw: block, // untouched — used to extract per-move [%clk] times
    };
  }).filter(g => g.moves.length > 0);
}

// ── Time-management analysis ──────────────────────────────────
// Pure math: code computes the numbers, Gemini only narrates them later.
// Chess.com PGNs annotate each move with remaining time as [%clk H:MM:SS.s].
// Seconds spent on a move = clock before that move − clock after it (for the
// same player), with the base time added back for the very first move.

function clkToSeconds(clk) {
  // "0:09:59.8" -> 599.8
  const parts = clk.split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return Number(clk) || 0;
}

// Base seconds from the TimeControl header ("600", "600+5", "180+2")
function baseSeconds(timeControl) {
  if (!timeControl) return null;
  const m = String(timeControl).match(/^(\d+)/);
  return m ? Number(m[1]) : null;
}

// Increment seconds from the TimeControl header ("600+5" -> 5, "600" -> 0)
function incrementSeconds(timeControl) {
  if (!timeControl) return 0;
  const m = String(timeControl).match(/\+(\d+)/);
  return m ? Number(m[1]) : 0;
}

// Returns an array of remaining-clock seconds, in move order (ply by ply),
// or null if this game has no clock data.
function extractClocks(rawBlock) {
  if (!rawBlock) return null;
  const clocks = [];
  const re = /\[%clk\s+([0-9:.]+)\]/g;
  let m;
  while ((m = re.exec(rawBlock)) !== null) clocks.push(clkToSeconds(m[1]));
  return clocks.length ? clocks : null;
}

// Phase by ply index (0-based). Opening = first 12 full moves (24 plies),
// endgame = move 30+ ; otherwise middlegame. Simple, robust, ELO-agnostic.
function phaseForPly(plyIndex) {
  const fullMove = Math.floor(plyIndex / 2) + 1;
  if (fullMove <= 12) return "opening";
  if (fullMove >= 30) return "endgame";
  return "middlegame";
}

// Build per-move seconds-spent for ONE player in a game.
// With increment, the clock is topped up by `inc` after each move, so the
// true thinking time is (clock before) − (clock after) + increment. For a
// 0-increment game inc=0 and this is just before − after.
function playerMoveTimes(game) {
  const clocks = extractClocks(game.raw);
  if (!clocks) return null;
  const base = baseSeconds(game.timeControl);
  const inc = incrementSeconds(game.timeControl);
  const color = game.playerColor || "w";
  // White moves are plies 0,2,4..., Black are 1,3,5...
  const startPly = color === "w" ? 0 : 1;
  const times = [];
  for (let ply = startPly; ply < clocks.length; ply += 2) {
    const prev = ply >= 2 ? clocks[ply - 2] : base; // clock before this move
    if (prev == null) continue;
    // First move gets no increment added back yet (you haven't completed a move)
    const incThisMove = ply >= 2 ? inc : 0;
    const spent = prev - clocks[ply] + incThisMove;
    times.push({
      ply,
      fullMove: Math.floor(ply / 2) + 1,
      phase: phaseForPly(ply),
      spent: Math.max(0, Math.round(spent * 10) / 10),
      remaining: clocks[ply],
    });
  }
  return times;
}

function analyzeTimeManagement(games, tagged) {
  const totals = {
    opening:    { spent: 0, moves: 0 },
    middlegame: { spent: 0, moves: 0 },
    endgame:    { spent: 0, moves: 0 },
  };
  let gamesWithClocks = 0;
  let lowTimeBlunders = 0;     // blunders made under a format-relative low-clock cutoff
  let totalBlunders = 0;
  let lowestEndgameClock = null;
  let totalBaseTime = null;
  const baseTimes = [];        // base time of each clocked game, to scale thresholds

  for (let gi = 0; gi < games.length; gi++) {
    const game = games[gi];
    const times = playerMoveTimes(game);
    if (!times) continue;
    gamesWithClocks++;
    const gameBase = baseSeconds(game.timeControl);
    if (gameBase) { baseTimes.push(gameBase); totalBaseTime = gameBase; }

    // Low-clock cutoff scales with the game: ~8% of base time, floored at 10s
    // and capped at 30s. (Bullet 60s → ~10s; blitz 180s → ~14s; rapid 600s → 30s.)
    const lowClockCutoff = gameBase ? Math.min(30, Math.max(10, gameBase * 0.08)) : 30;

    for (const t of times) {
      totals[t.phase].spent += t.spent;
      totals[t.phase].moves += 1;
      if (t.phase === "endgame") {
        if (lowestEndgameClock == null || t.remaining < lowestEndgameClock) lowestEndgameClock = t.remaining;
      }
    }

    // Cross-reference the player's blunders with how much time was left
    const blundersThisGame = tagged.filter(x => x.gameIndex === gi && x.severityType === "blunder");
    for (const b of blundersThisGame) {
      totalBlunders++;
      const atMove = times.find(t => t.fullMove === b.moveNumber);
      if (atMove && atMove.remaining <= lowClockCutoff) lowTimeBlunders++;
    }
  }

  if (gamesWithClocks === 0) return null; // no clock data in these PGNs

  const totalSpent = totals.opening.spent + totals.middlegame.spent + totals.endgame.spent;
  const pct = phase => totalSpent > 0 ? Math.round((totals[phase].spent / totalSpent) * 100) : 0;
  const avg = phase => totals[phase].moves > 0 ? Math.round(totals[phase].spent / totals[phase].moves) : 0;

  const openingPct = pct("opening");
  const endgamePct = pct("endgame");

  // ── Format-relative thresholds ──
  // "Fair share" per move = base time / an assumed ~40-move game. Flags compare
  // against this instead of fixed seconds, so they're meaningful for bullet,
  // blitz, and rapid alike. Falls back to rapid-ish defaults if base unknown.
  const avgBase = baseTimes.length ? baseTimes.reduce((s, b) => s + b, 0) / baseTimes.length : 600;
  const fairShare = avgBase / 40;                       // even pace per move
  const lowEndgameCutoff = Math.min(30, Math.max(10, avgBase * 0.08));

  const flags = [];
  // Slow opening: averaging well over a fair share early, or eating 45%+ of the clock
  if (avg("opening") >= fairShare * 1.6 || openingPct >= 45) {
    flags.push("slow_opening");
  }
  // Rushed opening: barely thinking — under 40% of a fair share per move
  if (totals.opening.moves >= 6 && avg("opening") <= fairShare * 0.4) {
    flags.push("rushed_opening");
  }
  // Endgame time trouble: reached the endgame near the format-relative cutoff
  if (lowestEndgameClock != null && lowestEndgameClock <= lowEndgameCutoff && totals.endgame.moves > 0) {
    flags.push("endgame_time_trouble");
  }
  // Blunders cluster when the clock is low
  if (totalBlunders > 0 && lowTimeBlunders / totalBlunders >= 0.4) {
    flags.push("blunders_in_time_pressure");
  }

  return {
    gamesWithClocks,
    baseTime: totalBaseTime,
    mixedFormats: new Set(baseTimes).size > 1, // were different time controls pooled?
    byPhase: {
      opening:    { avgSeconds: avg("opening"),    pctOfTime: openingPct,  moves: totals.opening.moves },
      middlegame: { avgSeconds: avg("middlegame"), pctOfTime: pct("middlegame"), moves: totals.middlegame.moves },
      endgame:    { avgSeconds: avg("endgame"),    pctOfTime: endgamePct,  moves: totals.endgame.moves },
    },
    lowestEndgameClock,
    lowTimeBlunders,
    totalBlunders,
    flags,
  };
}

// Turn the computed time facts into 2–3 sentences of coaching (Gemini narrates,
// never calculates).
async function explainTimeManagement(tm, playerElo) {
  if (!tm) return "";
  const facts =
    `Player ELO: ${playerElo}. Time control base: ${tm.baseTime ? tm.baseTime + "s" : "unknown"}.\n` +
    `Average seconds per move by phase — opening: ${tm.byPhase.opening.avgSeconds}s ` +
    `(${tm.byPhase.opening.pctOfTime}% of total time), ` +
    `middlegame: ${tm.byPhase.middlegame.avgSeconds}s (${tm.byPhase.middlegame.pctOfTime}%), ` +
    `endgame: ${tm.byPhase.endgame.avgSeconds}s (${tm.byPhase.endgame.pctOfTime}%).\n` +
    `Lowest clock reached in an endgame: ${tm.lowestEndgameClock != null ? tm.lowestEndgameClock + "s" : "n/a"}.\n` +
    `Blunders made with under 30s left: ${tm.lowTimeBlunders} of ${tm.totalBlunders} total blunders.\n` +
    `Detected patterns: ${tm.flags.length ? tm.flags.join(", ") : "none"}.`;

  const prompt =
    "You are a chess coach reviewing a student's CLOCK usage (not their moves). " +
    "Below are real, pre-computed statistics — treat every number as fact, do not invent any.\n\n" +
    facts + "\n\n" +
    "Write 2-3 short sentences of specific, actionable coaching about their TIME MANAGEMENT by game phase. " +
    "Point out the single biggest issue (e.g. spending too long in the opening, or reaching the endgame in time trouble) " +
    "and give one concrete habit to fix it. Be direct and encouraging. Start immediately, no preamble.";

  const out = await callGemini(prompt);
  return (out || "").trim();
}

// ── Aggregate ─────────────────────────────────────────────────
function aggregateResults(tagged) {
  const map = {};
  for (const t of tagged) {
    // Use AI label as the grouping key
    const key = t.tag || "Tactical Miscalculation";
    if (!map[key]) map[key] = { tag: key, label: key, frequency: 0, totalSeverity: 0, examples: [] };
    map[key].frequency++;
    map[key].totalSeverity += t.severity;
    if (map[key].examples.length < 3) map[key].examples.push(t);
  }
  return Object.values(map).map(w => ({
    ...w,
    avgSeverity: Math.round(w.totalSeverity / w.frequency),
    avgPawnsLost: (Math.round(w.totalSeverity / w.frequency) / 100).toFixed(1),
    classification: w.avgSeverity >= 150 || w.frequency >= 3 ? "CRITICAL_LEAK" : "GROWTH_MILESTONE",
  })).sort((a, b) => b.avgSeverity - a.avgSeverity);
}

// ── Board Example ─────────────────────────────────────────────
function BoardExample({ example, index, game, playerElo }) {
  const [view, setView] = useState("before");
  const [explanation, setExplanation] = useState(example.explanation || null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [tacticalFollowUp, setTacticalFollowUp] = useState(null);
  const [loadingTactical, setLoadingTactical] = useState(false);
  const [tacticalMoveIdx, setTacticalMoveIdx] = useState(0);
  const [showTactical, setShowTactical] = useState(false);

  async function loadExplanation() {
    setLoadingExplanation(true);
    setExplanation(null);
    // Try up to 2 times
    let text = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      text = await getMoveExplanation(example, playerElo);
      if (text && text.length > 20) break;
      if (attempt === 0) await new Promise(r => setTimeout(r, 800));
    }
    setExplanation(text || "failed");
    if (text && text.length > 20) example.explanation = text;
    setLoadingExplanation(false);
  }

  async function loadTacticalFollowUp() {
    if (tacticalFollowUp) { setShowTactical(true); return; }
    setLoadingTactical(true);
    const result = await getTacticalFollowUp(example.fen, example.bestUci, playerElo);
    setTacticalFollowUp(result);
    setTacticalMoveIdx(0);
    setShowTactical(true);
    setLoadingTactical(false);
  }

  // Which FEN to display
  let displayFen = example.fen;
  if (view === "after") displayFen = example.fenAfter;
  if (view === "best" && example.bestFrom && example.bestTo) {
    try {
      const chess = new Chess(example.fen);
      chess.move({ from: example.bestFrom, to: example.bestTo, promotion: "q" });
      displayFen = chess.fen();
    } catch { displayFen = example.fen; }
  }
  if (showTactical && tacticalFollowUp?.moves) {
    const idx = Math.min(tacticalMoveIdx, tacticalFollowUp.moves.length - 1);
    // Build FEN after tactical move idx
    try {
      const g = new Chess(example.fen);
      for (let i = 0; i <= idx; i++) {
        const m = tacticalFollowUp.moves[i];
        g.move({ from: m.from, to: m.to, promotion: "q" });
      }
      displayFen = g.fen();
    } catch {}
  }

  // Arrows
  const arrows = [];
  if (view === "before" && !showTactical) {
    if (example.from && example.to) arrows.push([example.from, example.to, "rgb(200,60,60)"]);
    if (example.bestFrom && example.bestTo) arrows.push([example.bestFrom, example.bestTo, "rgb(50,180,80)"]);
  }
  if (showTactical && tacticalFollowUp?.moves) {
    const idx = Math.min(tacticalMoveIdx, tacticalFollowUp.moves.length - 1);
    const m = tacticalFollowUp.moves[idx];
    if (m) arrows.push([m.from, m.to, "rgb(50,180,80)"]);
  }

  // Square highlights
  const squareStyles = {};
  if (view === "before" && !showTactical) {
    if (example.from) squareStyles[example.from] = { backgroundColor: "rgba(200,60,60,0.35)" };
    if (example.to)   squareStyles[example.to]   = { backgroundColor: "rgba(200,60,60,0.35)" };
  }

  const evalStr = (cp) => {
    if (cp > 900) return "Winning";
    if (cp < -900) return "Losing";
    if (Math.abs(cp) < 20) return "Equal";
    const val = Math.abs(cp / 100).toFixed(1);
    return cp > 0 ? "Winning (+" + val + ")" : "Losing (-" + val + ")";
  };

  const hasTactical = !!example.bestSan; // show for all mistakes with a better move

  return (
    <div className="board-example">
      <div className="example-top">
        <span className={"severity-badge " + example.severityType}>
          {example.severityType === "blunder" ? "⚠️ Blunder" : example.severityType === "mistake" ? "❗ Mistake" : "⚡ Inaccuracy"}
        </span>
        <span className="example-game-label">
          {game ? game.white + " vs " + game.black : "Game " + (example.gameIndex + 1)}
        </span>
        <span className="example-move-num">Move {example.moveNumber}</span>
        <span className="eval-change">
          {evalStr(example.evalBeforeCp)} → {evalStr(example.evalAfterCp)}
        </span>
      </div>

      {/* Tactical follow-up viewer */}
      {showTactical && tacticalFollowUp ? (
        <div className="tactical-viewer">
          <div className="tactical-header">
            <span className="tactical-label">♟ Tactical line: {tacticalFollowUp.moves.map(m => m.san).join(" → ")}</span>
            <button className="btn btn-secondary" style={{ fontSize:11, padding:"4px 10px" }}
              onClick={() => setShowTactical(false)}>← Back</button>
          </div>
          <div className="example-board-wrap">
            <Chessboard
              position={displayFen}
              arePiecesDraggable={false}
              boardOrientation={example.moveColor === "w" ? "white" : "black"}
              customArrows={arrows}
              customSquareStyles={squareStyles}
              customBoardStyle={{ borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.5)" }}
              customLightSquareStyle={{ backgroundColor: "#f0d9b5" }}
              customDarkSquareStyle={{ backgroundColor: "#b58863" }}
            />
          </div>
          <div className="tactical-nav">
            {tacticalFollowUp.moves.map((m, i) => (
              <button key={i}
                className={"tactical-move-btn " + (tacticalMoveIdx === i ? "active" : "")}
                onClick={() => setTacticalMoveIdx(i)}>
                {i + 1}. {m.san}
              </button>
            ))}
          </div>
          {tacticalFollowUp.explanation && (
            <div className="explanation-text" style={{ marginTop: 8 }}>
              <span className="explanation-icon">♟</span>
              <p>{tacticalFollowUp.explanation}</p>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="example-board-wrap">
            <Chessboard
              position={displayFen}
              arePiecesDraggable={false}
              boardOrientation={example.moveColor === "w" ? "white" : "black"}
              customArrows={arrows}
              customSquareStyles={squareStyles}
              customBoardStyle={{ borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.5)" }}
              customLightSquareStyle={{ backgroundColor: "#f0d9b5" }}
              customDarkSquareStyle={{ backgroundColor: "#b58863" }}
            />
          </div>

          <div className="view-buttons">
            <button className={"view-btn " + (view === "before" ? "active" : "")} onClick={() => setView("before")}>Before move</button>
            <button className={"view-btn " + (view === "after" ? "active" : "")} onClick={() => setView("after")}>
              ❌ You played: <strong>{example.san}</strong>
            </button>
            {example.bestSan && (
              <button className={"view-btn good " + (view === "best" ? "active" : "")} onClick={() => setView("best")}>
                ✅ What's better: <strong>{example.bestSan}</strong>
              </button>
            )}
          </div>

          {view === "before" && (
            <div className="arrows-legend">
              <span className="arrow-red">━━▶</span> You played: <strong>{example.san}</strong>
              {example.bestSan && <><span className="arrow-green" style={{ marginLeft: 12 }}>━━▶</span> Better: <strong>{example.bestSan}</strong></>}
            </div>
          )}
        </>
      )}

      {/* Explanation box */}
      <div className="explanation-box">
        {!explanation && !loadingExplanation && (
          <button className="btn btn-secondary explain-btn" onClick={loadExplanation}>
            💡 Explain why this is wrong
          </button>
        )}
        {loadingExplanation && (
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 0" }}>
            <div className="spinner" style={{ width:16, height:16, borderWidth:2 }} />
            <p className="loading-explanation">Analysing position with AI...</p>
          </div>
        )}
        {explanation && explanation !== "failed" && (
          <div>
            <div className="explanation-text">
              <span className="explanation-icon">💡</span>
              <p>{explanation}</p>
            </div>
            <button className="btn btn-secondary" style={{ fontSize:11, padding:"4px 10px", marginTop:6 }}
              onClick={loadExplanation}>
              🔄 Regenerate
            </button>
          </div>
        )}
        {explanation === "failed" && (
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:12, color:"var(--text-muted)" }}>Could not generate — </span>
            <button className="btn btn-secondary" style={{ fontSize:11, padding:"4px 10px" }}
              onClick={loadExplanation}>
              🔄 Try again
            </button>
          </div>
        )}
      </div>

      {/* Tactical follow-up button */}
      {example.bestSan && !showTactical && (
        <button
          className="btn btn-secondary"
          style={{ fontSize: 12, padding: "7px 14px", marginTop: 4, borderColor: "rgba(50,180,80,0.4)", color: "#52c97a" }}
          onClick={loadTacticalFollowUp}
          disabled={loadingTactical}>
          {loadingTactical ? "Loading tactic..." : "♟ Show tactical follow-up"}
        </button>
      )}
    </div>
  );
}


// ── Weakness Card ─────────────────────────────────────────────
function WeaknessCard({ weakness, rank, type, isExpanded, onToggle, games, playerElo, onAITag }) {
  const [aiLabel, setAiLabel] = React.useState(weakness.label);
  const [tagging, setTagging] = React.useState(false);

  async function handleExpand() {
    onToggle();
    // Lazy AI tag — only when user first opens the card
    if (!isExpanded && weakness._aiTagged !== true && weakness.examples?.length > 0) {
      setTagging(true);
      const ex = weakness.examples[0];
      const tag = await getAIWeaknessTag(
        ex.fen, ex.san, ex.bestSan,
        ex.evalBeforeCp, ex.evalAfterCp,
        ex.severityType, ex.moveNumber, playerElo
      );
      if (tag) {
        setAiLabel(tag);
        weakness.label = tag;
        weakness._aiTagged = true;
        if (onAITag) onAITag(tag);
      }
      setTagging(false);
    }
  }

  return (
    <div className={`weakness-card ${type} ${isExpanded ? "expanded" : ""}`}>
      <div className="wcard-header" onClick={handleExpand}>
        <div className={`wcard-rank ${type}`}>#{rank}</div>
        <div className="wcard-info">
          <div className="wcard-label">{aiLabel}{tagging && <span style={{fontSize:10,color:"var(--text-muted)",marginLeft:6}}>identifying...</span>}</div>
          <div className="wcard-stats">
            Found <strong>{weakness.frequency}</strong> time{weakness.frequency !== 1 ? "s" : ""} &nbsp;·&nbsp;
            avg <strong>{weakness.avgPawnsLost} pawns</strong> lost per mistake
          </div>
        </div>
        <div className="wcard-chevron">{isExpanded ? "▲" : "▼ See positions"}</div>
      </div>
      {isExpanded && (
        <div className="wcard-body">
          <p className="examples-intro">
            Red arrow shows what you played. Green arrow shows what's better.
            Click "Explain why this is wrong" on any position for a detailed breakdown.
          </p>
          <div className="examples-boards">
            {weakness.examples.map((ex, i) => (
              <BoardExample key={i} example={ex} index={i} game={games[ex.gameIndex]} playerElo={playerElo} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Time Management Panel ─────────────────────────────────────
const TM_FLAG_LABEL = {
  slow_opening: "🐌 Slow in the opening — burning the clock early",
  rushed_opening: "⚡ Rushing the opening — moving too fast to think",
  endgame_time_trouble: "⏳ Reaching the endgame in time trouble",
  blunders_in_time_pressure: "💥 Blunders cluster when the clock is low",
};

function TimeManagementPanel({ tm, playerElo, coaching, setCoaching }) {
  const [loadingEx, setLoadingEx] = useState(false);
  const explanation = coaching;

  if (!tm) {
    return (
      <div className="curriculum-panel">
        <h3>⏱️ Time Management</h3>
        <p className="curriculum-sub">These games have no clock data, so per-phase timing can't be analyzed. Export games from Chess.com or Lichess with clock times included.</p>
      </div>
    );
  }

  const phases = [
    { key: "opening", label: "Opening", cls: "phase-leak" },
    { key: "middlegame", label: "Middlegame", cls: "" },
    { key: "endgame", label: "Endgame", cls: "phase-growth" },
  ];

  async function getCoaching() {
    setLoadingEx(true);
    try {
      const txt = await explainTimeManagement(tm, playerElo);
      setCoaching(txt || "Couldn't generate coaching — try again.");
    } catch { setCoaching("Couldn't generate coaching — try again."); }
    finally { setLoadingEx(false); }
  }

  return (
    <div className="curriculum-panel">
      <h3>⏱️ Time Management</h3>
      <p className="curriculum-sub">
        How you spend your clock across the game ({tm.gamesWithClocks} game{tm.gamesWithClocks > 1 ? "s" : ""} with clock data)
      </p>
      {tm.mixedFormats && (
        <p className="tm-stat" style={{ marginBottom: 10 }}>
          ⚠️ These games span different time controls, so per-move averages blend formats. Pattern flags are scaled to each game and remain reliable.
        </p>
      )}

      <div className="tm-phases">
        {phases.map(p => {
          const d = tm.byPhase[p.key];
          return (
            <div key={p.key} className={`tm-phase ${p.cls}`}>
              <div className="tm-phase-label">{p.label}</div>
              <div className="tm-phase-avg">{d.avgSeconds}s<span>/move</span></div>
              <div className="tm-bar"><div className="tm-bar-fill" style={{ width: `${d.pctOfTime}%` }} /></div>
              <div className="tm-phase-pct">{d.pctOfTime}% of total time</div>
            </div>
          );
        })}
      </div>

      {tm.flags.length > 0 && (
        <div className="tm-flags">
          {tm.flags.map(f => (
            <div key={f} className="tm-flag">{TM_FLAG_LABEL[f] || f}</div>
          ))}
        </div>
      )}

      {tm.totalBlunders > 0 && (
        <p className="tm-stat">
          {tm.lowTimeBlunders} of {tm.totalBlunders} blunder{tm.totalBlunders > 1 ? "s" : ""} happened with under 30s on the clock.
        </p>
      )}

      <div className="explanation-box" style={{ marginTop: 12 }}>
        {!explanation && !loadingEx && (
          <button className="btn btn-secondary explain-btn" onClick={getCoaching}>💬 Get coaching on this</button>
        )}
        {loadingEx && <p className="loading-explanation">Coach is reviewing your clock usage…</p>}
        {explanation && (
          <div className="explanation-text">
            <span className="explanation-icon">⏱️</span>
            <p>{explanation}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Curriculum Panel ──────────────────────────────────────────
function CurriculumPanel({ curriculum, loading, playerElo, targetElo, onRegenerate, timeManagement, tmCoaching, username }) {
  if (loading) return (
    <div className="curriculum-panel">
      <h3>📚 Generating Your Training Plan...</h3>
      <p className="curriculum-sub">Claude AI is building a personalized curriculum</p>
      <div className="spinner" style={{ margin: "20px auto" }} />
    </div>
  );

  if (!curriculum || curriculum.length === 0) return (
    <div className="curriculum-panel">
      <h3>📚 Training Plan</h3>
      <p className="curriculum-sub">Click "Generate Plan" to build your training curriculum.</p>
      <button className="btn btn-primary" style={{ marginTop:8 }}
        onClick={onRegenerate}>
        📚 Generate Training Plan
      </button>
    </div>
  );

  // Build a polished, branded PDF: header band, time-management section
  // (phase stats + AI coaching when generated), then the curriculum.
  function downloadCurriculumPDF() {
    const docpdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = docpdf.internal.pageSize.getWidth();
    const pageH = docpdf.internal.pageSize.getHeight();
    const margin = 50;
    const contentW = pageW - margin * 2;

    // Palette (matches the app's gold/cream theme)
    const GOLD = [184, 138, 50];
    const GOLD_SOFT = [241, 230, 200];
    const INK = [38, 38, 42];
    const GREY = [110, 110, 118];
    const LEAK = [200, 70, 70];
    const GROWTH = [70, 150, 90];

    let y = 0;

    const footer = () => {
      const n = docpdf.internal.getNumberOfPages();
      for (let i = 1; i <= n; i++) {
        docpdf.setPage(i);
        docpdf.setFont("helvetica", "normal");
        docpdf.setFontSize(8);
        docpdf.setTextColor(160, 160, 165);
        docpdf.text("Generated by ChessingIt — AI Game Analysis", margin, pageH - 24);
        docpdf.text(`Page ${i} of ${n}`, pageW - margin, pageH - 24, { align: "right" });
      }
    };

    const need = (h) => { if (y + h > pageH - 50) { docpdf.addPage(); y = margin; } };

    const write = (text, { size = 11, style = "normal", color = INK, gap = 4, indent = 0, lh = 1.4 } = {}) => {
      docpdf.setFont("helvetica", style);
      docpdf.setFontSize(size);
      docpdf.setTextColor(...color);
      const lines = docpdf.splitTextToSize(String(text), contentW - indent);
      const lineH = size * lh;
      need(lines.length * lineH);
      docpdf.text(lines, margin + indent, y);
      y += lines.length * lineH + gap;
    };

    const sectionHeading = (label) => {
      need(40);
      y += 6;
      docpdf.setFillColor(...GOLD);
      docpdf.rect(margin, y - 9, 4, 16, "F"); // gold tab
      docpdf.setFont("helvetica", "bold");
      docpdf.setFontSize(15);
      docpdf.setTextColor(...INK);
      docpdf.text(label, margin + 12, y + 3);
      y += 22;
    };

    // ── Header band ──
    docpdf.setFillColor(...INK);
    docpdf.rect(0, 0, pageW, 96, "F");
    docpdf.setFont("helvetica", "bold");
    docpdf.setFontSize(24);
    docpdf.setTextColor(...GOLD);
    docpdf.text("Chess Performance Report", margin, 50);
    docpdf.setFont("helvetica", "normal");
    docpdf.setFontSize(11);
    docpdf.setTextColor(220, 218, 210);
    const sub = `${username ? username + "  ·  " : ""}${playerElo} → ${targetElo} ELO  ·  ${new Date().toLocaleDateString()}`;
    docpdf.text(sub, margin, 72);
    y = 96 + 30;

    // ── Time Management section ──
    if (timeManagement) {
      const tm = timeManagement;
      sectionHeading("Time Management");

      write(`Based on ${tm.gamesWithClocks} game${tm.gamesWithClocks > 1 ? "s" : ""} with clock data.`,
        { size: 10, color: GREY, gap: 10 });

      // Phase cards in a row
      const phases = [
        { key: "opening", label: "OPENING", color: LEAK },
        { key: "middlegame", label: "MIDDLEGAME", color: GOLD },
        { key: "endgame", label: "ENDGAME", color: GROWTH },
      ];
      const cardW = (contentW - 24) / 3;
      const cardH = 70;
      need(cardH + 10);
      phases.forEach((p, i) => {
        const d = tm.byPhase[p.key];
        const x = margin + i * (cardW + 12);
        docpdf.setFillColor(248, 246, 240);
        docpdf.roundedRect(x, y, cardW, cardH, 6, 6, "F");
        docpdf.setFont("helvetica", "bold");
        docpdf.setFontSize(8);
        docpdf.setTextColor(...p.color);
        docpdf.text(p.label, x + 12, y + 18);
        docpdf.setFontSize(22);
        docpdf.setTextColor(...INK);
        docpdf.text(`${d.avgSeconds}s`, x + 12, y + 42);
        docpdf.setFont("helvetica", "normal");
        docpdf.setFontSize(8);
        docpdf.setTextColor(...GREY);
        docpdf.text(`per move · ${d.pctOfTime}% of time`, x + 12, y + 58);
      });
      y += cardH + 16;

      // Flags
      const FLAG_TEXT = {
        slow_opening: "Spends too long in the opening — burning the clock early",
        rushed_opening: "Rushes the opening — moving too fast to think",
        endgame_time_trouble: "Reaches the endgame in time trouble",
        blunders_in_time_pressure: "Blunders cluster when the clock is low",
      };
      if (tm.flags?.length) {
        write("Patterns detected:", { size: 10.5, style: "bold", color: INK, gap: 5 });
        tm.flags.forEach(f => write(`•  ${FLAG_TEXT[f] || f}`, { size: 10, color: LEAK, gap: 3, indent: 10 }));
        y += 4;
      }
      if (tm.totalBlunders > 0) {
        write(`${tm.lowTimeBlunders} of ${tm.totalBlunders} blunder${tm.totalBlunders > 1 ? "s" : ""} occurred with very little time on the clock.`,
          { size: 10, color: GREY, gap: 6 });
      }

      // AI coaching paragraph — only if it was generated
      if (tmCoaching && !tmCoaching.startsWith("Couldn't")) {
        need(40);
        docpdf.setFillColor(...GOLD_SOFT);
        const lines = docpdf.splitTextToSize(tmCoaching, contentW - 28);
        const boxH = lines.length * 13 + 24;
        need(boxH);
        docpdf.roundedRect(margin, y, contentW, boxH, 6, 6, "F");
        docpdf.setFont("helvetica", "italic");
        docpdf.setFontSize(10);
        docpdf.setTextColor(90, 70, 30);
        docpdf.text(lines, margin + 14, y + 18);
        y += boxH + 14;
      }
    }

    // ── Training Plan section ──
    sectionHeading("Training Plan");
    write(`${curriculum.length}-week curriculum, prioritized by the biggest gains.`,
      { size: 10, color: GREY, gap: 12 });

    curriculum.forEach((w) => {
      const isLeak = w.phase?.includes("Plug");
      const accent = isLeak ? LEAK : GROWTH;
      const phaseLabel = isLeak ? "FIX FIRST" : "LEVEL UP";

      // Measure-ish: ensure the header + topic fit together
      need(60);
      const cardTop = y;
      // accent bar
      docpdf.setFillColor(...accent);
      docpdf.rect(margin, y, 3, 14, "F");
      docpdf.setFont("helvetica", "bold");
      docpdf.setFontSize(8);
      docpdf.setTextColor(...accent);
      docpdf.text(`WEEK ${w.week}  ·  ${phaseLabel}`, margin + 12, y + 11);
      y += 24;

      write(w.topic || "", { size: 13, style: "bold", color: INK, gap: 5, indent: 12 });
      if (w.explanation) write(w.explanation, { size: 10, color: [80, 80, 86], gap: 6, indent: 12 });
      if (w.goal) write(`Goal: ${w.goal}`, { size: 10, style: "bold", color: GOLD, gap: 6, indent: 12 });

      if (w.dailyExercises?.length) {
        write("Daily practice", { size: 8.5, style: "bold", color: GREY, gap: 3, indent: 12 });
        w.dailyExercises.forEach(ex => write(`•  ${ex}`, { size: 10, color: [70, 70, 76], gap: 2, indent: 22 }));
        y += 3;
      }
      if (w.resources?.length) {
        write("Resources", { size: 8.5, style: "bold", color: GREY, gap: 3, indent: 12 });
        w.resources.forEach(r => write(`–  ${r}`, { size: 10, color: [70, 70, 76], gap: 2, indent: 22 }));
      }
      y += 14;
    });

    footer();
    const safeName = (username || "player").replace(/[^a-z0-9]/gi, "_").toLowerCase();
    docpdf.save(`chessingit-report-${safeName}-${playerElo}elo.pdf`);
  }

  return (
    <div className="curriculum-panel">
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
        <h3>📚 Your Training Plan</h3>
        <div style={{ display:"flex", gap:6 }}>
          <button className="btn btn-secondary" style={{ fontSize:11, padding:"5px 12px" }}
            onClick={downloadCurriculumPDF}>⬇️ Download Report PDF</button>
          <button className="btn btn-secondary" style={{ fontSize:11, padding:"5px 12px" }}
            onClick={onRegenerate}>🔄 New Plan</button>
        </div>
      </div>
      <p className="curriculum-sub">{playerElo} → {targetElo} ELO · {curriculum.length} weeks</p>
      <div className="curriculum-weeks">
        {curriculum.map((w, i) => (
          <div key={i} className={`curriculum-week ${w.phase?.includes("Plug") ? "phase-leak" : "phase-growth"}`}>
            <div className="week-header">
              <div className="week-number">Week {w.week}</div>
              <div className="week-phase">{w.phase?.includes("Plug") ? "🔧 Fix First" : "⬆️ Level Up"}</div>
            </div>
            <div className="week-topic">{w.topic}</div>
            {w.explanation && <div className="week-why">{w.explanation}</div>}
            {w.goal && <div className="week-goal">🎯 {w.goal}</div>}
            {w.dailyExercises?.length > 0 && (
              <div className="week-exercises">
                <div className="exercises-label">Daily practice:</div>
                {w.dailyExercises.map((ex, j) => <div key={j} className="exercise-item">• {ex}</div>)}
              </div>
            )}
            {w.resources?.length > 0 && (
              <div className="week-resources">
                <div className="resources-label">Resources:</div>
                {w.resources.map((r, j) => <div key={j} className="resource-item">📖 {r}</div>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────
export default function AnalysisDashboard() {
  const [step, setStep] = useState("upload");
  const [pgn, setPgn] = useState("");
  const [elo, setElo] = useState(1000);
  const [username, setUsername] = useState("");
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");
  const [activeGame, setActiveGame] = useState(0);
  const [expandedWeakness, setExpandedWeakness] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [curriculumLoading, setCurriculumLoading] = useState(false);
  const [tmCoaching, setTmCoaching] = useState(null); // shared so the PDF can include it
  const [geminiStatus, setGeminiStatus] = useState(null); // null | "testing" | "ok" | "fail"
  // Find-my-games feature
  const [inputMode, setInputMode] = useState("paste"); // "paste" | "fetch"
  const [fetchSite, setFetchSite] = useState("lichess"); // "lichess" | "chesscom"
  const [fetchName, setFetchName] = useState("");
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [fetchedGames, setFetchedGames] = useState([]);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const fileRef = useRef();

  async function testGemini() {
    const apiKey = process.env.REACT_APP_GEMINI_KEY;
    console.log("Key loaded:", apiKey ? apiKey.slice(0,10) + "..." : "MISSING");
    setGeminiStatus("testing");
    try {
      // List available models first
      const modelsRes = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
      );
      const modelsData = await modelsRes.json();
      if (modelsData.models) {
        console.log("[Gemini] Available models:", modelsData.models.map(m => m.name));
        const flashes = modelsData.models.filter(m =>
          m.name.toLowerCase().includes("flash") &&
          m.supportedGenerationMethods?.includes("generateContent")
        );
        // Prefer the highest version number (e.g. 3.5 over 2.5), and prefer
        // non-lite, non-preview models so the curriculum gets the best quality.
        const score = (name) => {
          const n = name.toLowerCase();
          const ver = parseFloat((n.match(/(\d+\.\d+)/) || [0, 0])[1]) || 0;
          let s = ver * 10;
          if (n.includes("lite")) s -= 3;
          if (n.includes("preview") || n.includes("exp")) s -= 2;
          return s;
        };
        const flashModel = flashes.sort((a, b) => score(b.name) - score(a.name))[0];
        if (flashModel) {
          const modelId = flashModel.name.replace("models/", "");
          window._geminiModel = modelId;
          console.log("[Gemini] Using:", modelId);
          const result = await callGemini("Say: Gemini is working!");
          if (result && result.length > 3) {
            console.log("[Gemini Test] Success:", result);
            setGeminiStatus("ok");
            return;
          }
        } else {
          console.error("[Gemini] No flash model found:", modelsData.models.map(m => m.name));
        }
      } else {
        console.error("[Gemini] Could not list models:", modelsData);
      }
    } catch(e) {
      console.error("[Gemini Test] Error:", e);
    }
    setGeminiStatus("fail");
  }

  function handleFile(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    let combined = "", loaded = 0;
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => {
        combined += ev.target.result + "\n\n";
        if (++loaded === files.length) setPgn(combined.trim());
      };
      reader.readAsText(file);
    });
  }

  async function handleFetchGames() {
    if (!fetchName.trim()) return;
    setFetchLoading(true);
    setFetchError("");
    setFetchedGames([]);
    setSelectedGameIds([]);
    try {
      const games = await fetchRecentGames(fetchSite, fetchName.trim(), 10);
      setFetchedGames(games);
      // Use the searched username directly — no need to ask again.
      setUsername(fetchName.trim());
      // Auto-fill ELO from the player's rating in the most recent game.
      const recentRated = games.find(g => g.playerElo);
      if (recentRated) setElo(recentRated.playerElo);
    } catch (err) {
      setFetchError(err.message || "Couldn't fetch games. Check the username and try again.");
    } finally {
      setFetchLoading(false);
    }
  }

  function toggleGameSelect(id) {
    setSelectedGameIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      // Rebuild the PGN box from the selected games, in fetched order
      const chosen = fetchedGames.filter(g => next.includes(g.id));
      setPgn(chosen.map(g => g.pgn).join("\n\n"));
      return next;
    });
  }

  async function runAnalysis() {
    if (!pgn.trim()) return;
    setStep("analyzing");
    setProgress(5);
    setProgressMsg("Parsing your games...");
    setCurriculum(null);

    try {
      const games = parsePGNGames(pgn);
      if (!games.length) { alert("No valid games found."); setStep("upload"); return; }

      // Auto-detect username from PGN if not provided
      const effectiveUsername = username.trim() ||
        games[0]?.white || "Player";

      setProgress(10);
      setProgressMsg(`Found ${games.length} game(s). Calling Stockfish for every position — please wait...`);

      const { tagged, overallAccuracy, accuracyMoveCount } = await fullAnalyze(
        games, elo, effectiveUsername,
        p => { setProgress(10 + p); setProgressMsg(`Evaluating positions with Stockfish... (${10 + p}%)`); }
      );

      setProgress(93);
      setProgressMsg("Building report...");

      const weaknesses = aggregateResults(tagged);
      const thresholds = getThresholds(elo);
      const criticalLeaks = weaknesses.filter(w => w.classification === "CRITICAL_LEAK");
      const growthMilestones = weaknesses.filter(w => w.classification === "GROWTH_MILESTONE");

      // Time management (pure math from the [%clk] tags). null if PGNs have no clocks.
      const timeManagement = analyzeTimeManagement(games, tagged);

      const reportData = {
        meta: { playerElo: elo, targetElo: elo + 200, gamesAnalyzed: games.length, username: effectiveUsername },
        summary: {
          overallAccuracy,
          accuracyMoveCount,
          estimatedEloGain: Math.min(200, criticalLeaks.length * 35 + growthMilestones.length * 15),
          blunderCount: tagged.filter(t => t.severityType === "blunder").length,
          mistakeCount: tagged.filter(t => t.severityType === "mistake").length,
          inaccuracyCount: tagged.filter(t => t.severityType === "inaccuracy").length,
        },
        criticalLeaks, growthMilestones, games, tagged, timeManagement,
      };

      setProgress(100);
      setReport(reportData);
      setStep("results");

      // Curriculum is generated manually by user clicking the button
      // This saves Gemini tokens — user gets report first, requests curriculum when ready
      setCurriculum(null);
      setCurriculumLoading(false);

    } catch (e) {
      console.error(e);
      alert("Error: " + e.message);
      setStep("upload");
    }
  }

  const gameCount = pgn ? parsePGNGames(pgn).length : 0;

  return (
    <div className="analysis-page">
      <div className="analysis-header">
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <button className="btn btn-secondary" style={{ fontSize:13, padding:"8px 16px", flexShrink:0 }}
            onClick={() => window.history.back()}>← Back</button>
          <div>
            <h1>Game Analysis</h1>
            <p>Upload your games and get a personalized training plan</p>
          </div>
        </div>
        {step === "results" && (
          <button className="btn btn-secondary" onClick={() => { setStep("upload"); setReport(null); setPgn(""); }}>← New Analysis</button>
        )}
      </div>

      {/* UPLOAD */}
      {step === "upload" && (
        <div className="upload-section">

          {/* Mode toggle: paste/upload vs fetch by username */}
          <div className="input-mode-toggle">
            <button
              className={`mode-tab ${inputMode === "paste" ? "active" : ""}`}
              onClick={() => setInputMode("paste")}
            >📋 Upload / Paste PGN</button>
            <button
              className={`mode-tab ${inputMode === "fetch" ? "active" : ""}`}
              onClick={() => setInputMode("fetch")}
            >🔎 Find my games</button>
          </div>

          {inputMode === "fetch" && (
            <div className="fetch-panel">
              <div className="fetch-controls">
                <div className="fetch-site-pick">
                  <button className={`site-btn site-lichess ${fetchSite === "lichess" ? "active" : ""}`}
                    onClick={() => setFetchSite("lichess")}>
                    <svg viewBox="0 0 50 50" width="20" height="20" aria-hidden="true">
                      <path fill="currentColor" d="M9 39c0-7 7-9 9-14 1.5-3.5-1-5-1-8 0-5 4-9 9-9 2 0 3 1 4 2l9 7c2 1.5 3 4 3 7 0 8-3 12-3 16H9z"/>
                      <circle cx="22" cy="13" r="1.6" fill="#111"/>
                    </svg>
                    Lichess
                  </button>
                  <button className={`site-btn site-chesscom ${fetchSite === "chesscom" ? "active" : ""}`}
                    onClick={() => setFetchSite("chesscom")}>
                    <svg viewBox="0 0 50 50" width="20" height="20" aria-hidden="true">
                      <path fill="currentColor" d="M9 39c0-7 7-9 9-14 1.5-3.5-1-5-1-8 0-5 4-9 9-9 2 0 3 1 4 2l9 7c2 1.5 3 4 3 7 0 8-3 12-3 16H9z"/>
                      <circle cx="22" cy="13" r="1.6" fill="#fff"/>
                    </svg>
                    Chess.com
                  </button>
                </div>
                <input
                  className="fetch-name-input"
                  value={fetchName}
                  onChange={e => setFetchName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleFetchGames(); }}
                  placeholder={fetchSite === "lichess" ? "Lichess username" : "Chess.com username"}
                />
                <button className="btn btn-primary" onClick={handleFetchGames} disabled={fetchLoading || !fetchName.trim()}>
                  {fetchLoading ? "Fetching…" : "Get games"}
                </button>
              </div>

              <div className="fetch-tip">
                <span>💡</span>
                <p>For the most useful analysis, pick <strong>rapid or classical</strong> games — there's enough thinking time for your real chess to show. Bullet and blitz work too, but results are noisier.</p>
              </div>

              {fetchError && <p className="fetch-error">{fetchError}</p>}

              {fetchedGames.length > 0 && (
                <>
                  {/* Identity bar — username + auto-detected rating, editable */}
                  <div className="fetch-identity">
                    <div className="fetch-id-user">
                      <span className="fetch-id-avatar">{(username || "?").charAt(0).toUpperCase()}</span>
                      <div>
                        <div className="fetch-id-name">{username}</div>
                        <div className="fetch-id-site">{fetchSite === "lichess" ? "Lichess" : "Chess.com"}</div>
                      </div>
                    </div>
                    <div className="fetch-id-elo">
                      <label>Your rating</label>
                      <div className="elo-input-wrap">
                        <input type="number" value={elo} min={400} max={3000}
                          onChange={e => setElo(parseInt(e.target.value) || 1000)} className="elo-input" />
                        <span className="elo-label">ELO</span>
                      </div>
                    </div>
                  </div>

                  <div className="fetch-results">
                    <div className="fetch-results-head">
                      <span>Select the games to analyze</span>
                      <span className="fetch-sel-count">{selectedGameIds.length} selected</span>
                    </div>
                    {fetchedGames.map(g => {
                      const sel = selectedGameIds.includes(g.id);
                      const recommended = g.timeClass === "rapid" || g.timeClass === "classical";
                      const oppName = g.white.toLowerCase() === (username || "").toLowerCase() ? g.black : g.white;
                      return (
                        <div key={g.id} className={`fetch-game-row ${sel ? "selected" : ""}`} onClick={() => toggleGameSelect(g.id)}>
                          <span className={`fetch-check ${sel ? "on" : ""}`}>{sel ? "✓" : ""}</span>
                          <span className={`fetch-outcome ${g.outcome}`}>{g.outcome === "win" ? "W" : g.outcome === "loss" ? "L" : "D"}</span>
                          <span className="fetch-players">vs {oppName}</span>
                          <span className={`fetch-tc ${recommended ? "good" : ""}`}>{g.timeClass}</span>
                          <span className="fetch-date">{g.date}</span>
                        </div>
                      );
                    })}
                    {pgn && <p className="upload-success" style={{ marginTop: 10 }}>✓ {gameCount} game{gameCount !== 1 ? "s" : ""} ready to analyze</p>}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="upload-grid">
            {inputMode === "paste" && (
            <div className="upload-card">
              <div className="upload-icon">♟</div>
              <h3>Upload PGN File(s)</h3>
              <p>Select one or multiple .pgn files. From Chess.com: open game → Share → Download PGN</p>
              <input ref={fileRef} type="file" accept=".pgn" multiple onChange={handleFile} style={{ display: "none" }} />
              <button className="btn btn-secondary" onClick={() => fileRef.current.click()}>Choose Files</button>
              {pgn && <p className="upload-success">✓ {gameCount} game{gameCount !== 1 ? "s" : ""} loaded</p>}
            </div>
            )}

            {inputMode === "paste" && (
            <div className="upload-card">
              <div className="upload-icon">📋</div>
              <h3>Paste PGN Text</h3>
              <p>Or paste PGN directly. Supports multiple games separated by a blank line.</p>
              <textarea value={pgn} onChange={e => setPgn(e.target.value)}
                placeholder={`[Event "Casual Game"]\n[White "YourName"]\n...\n\n1. e4 e5 ...`}
                rows={6} style={{ fontFamily: "monospace", fontSize: 11, resize: "vertical" }} />
              {pgn && <p className="upload-success">✓ {gameCount} game{gameCount !== 1 ? "s" : ""} detected</p>}
            </div>
            )}

            {inputMode === "paste" && (
            <div className="upload-card">
              <div className="upload-icon">👤</div>
              <h3>Your Username</h3>
              <p>Enter your Chess.com or Lichess username so we know which color you played</p>
              <input value={username} onChange={e => setUsername(e.target.value)}
                placeholder="e.g. MagnusCarlsen" style={{ marginBottom: 8 }} />
              <p style={{ fontSize: 12, color: "var(--text-muted)" }}>If left blank we'll assume you played White</p>

              <div style={{ marginTop: 16 }}>
                <label style={{ marginBottom: 6 }}>Your ELO</label>
                <div className="elo-input-wrap">
                  <input type="number" value={elo} min={400} max={2800}
                    onChange={e => setElo(parseInt(e.target.value) || 1000)} className="elo-input" />
                  <span className="elo-label">ELO</span>
                </div>
                <div className="elo-band" style={{ marginTop: 8 }}>
                  {elo < 800 ? "Beginner" : elo < 1000 ? "Novice" : elo < 1200 ? "Intermediate" : elo < 1500 ? "Club Player" : "Advanced+"}
                </div>
              </div>
            </div>
            )}
          </div>

          <div className="upload-note">
            <span>⏱</span>
            <p>Stockfish evaluates every position — takes <strong>2–4 min per game</strong>. AI then generates your personalized training plan. Do not close the tab.</p>
          </div>

          <div style={{ textAlign: "center", marginTop: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <button className="btn btn-primary analyze-btn" onClick={runAnalysis} disabled={!pgn.trim()}>
              🔍 Analyze My Games
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button className="btn btn-secondary" style={{ fontSize: 12, padding: "6px 16px" }}
                onClick={testGemini} disabled={geminiStatus === "testing"}>
                {geminiStatus === "testing" ? "Testing..." : "🧪 Test AI Connection"}
              </button>
              {geminiStatus === "ok" && <span style={{ color: "var(--success)", fontSize: 13 }}>✓ Gemini is working!</span>}
              {geminiStatus === "fail" && <span style={{ color: "var(--danger)", fontSize: 13 }}>✗ Gemini not working — check your API key in .env file</span>}
            </div>
          </div>
        </div>
      )}

      {/* ANALYZING */}
      {step === "analyzing" && (
        <div className="analyzing-section">
          <div className="analyzing-card">
            <div className="analyzing-piece">♟</div>
            <h2>Analyzing your games...</h2>
            <p>{progressMsg}</p>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <p className="progress-label">{progress}%</p>
            <div className="analysis-steps">
              {[
                { label: "Parsing PGN", t: 10 },
                { label: "Stockfish evaluation", t: 30 },
                { label: "Tagging weaknesses", t: 85 },
                { label: "Building report", t: 95 },
              ].map((s, i) => (
                <div key={i} className={`analysis-step ${progress >= s.t ? "done" : ""}`}>
                  {progress >= s.t ? "✓" : "○"} {s.label}
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 16 }}>Do not close this tab</p>
          </div>
        </div>
      )}

      {/* RESULTS */}
      {step === "results" && report && (
        <div className="results-section">
          <div className="summary-bar">
            <div className="summary-stat">
              <div className="stat-value">{report.summary.overallAccuracy}%</div>
              <div className="stat-label">Accuracy</div>
              <div className="stat-hint">
                How close your moves were to the best possible move
                {typeof report.summary.accuracyMoveCount === "number" && report.summary.accuracyMoveCount < 10
                  ? " · ⚠️ based on few positions (engine was busy) — re-run for a firmer number"
                  : ""}
              </div>
            </div>
            <div className="summary-stat">
              <div className="stat-value">{report.meta.gamesAnalyzed}</div>
              <div className="stat-label">Games</div>
            </div>
            <div className="summary-stat">
              <div className="stat-value" style={{ color: "var(--danger)" }}>{report.summary.blunderCount}</div>
              <div className="stat-label">Blunders</div>
              <div className="stat-hint">Went from equal/winning to losing</div>
            </div>
            <div className="summary-stat">
              <div className="stat-value" style={{ color: "#f5a623" }}>{report.summary.mistakeCount}</div>
              <div className="stat-label">Mistakes</div>
              <div className="stat-hint">Significant advantage thrown away</div>
            </div>
            <div className="summary-stat">
              <div className="stat-value">{report.summary.inaccuracyCount}</div>
              <div className="stat-label">Inaccuracies</div>
              <div className="stat-hint">Slightly suboptimal moves</div>
            </div>
            <div className="summary-stat">
              <div className="stat-value" style={{ color: "var(--success)" }}>+{report.summary.estimatedEloGain}</div>
              <div className="stat-label">Est. ELO Gain</div>
              <div className="stat-hint">Potential gain by fixing these issues</div>
            </div>
          </div>

          <div className="results-grid">
            <div className="results-left">
              {report.criticalLeaks.length > 0 && (
                <div className="weakness-section">
                  <div className="section-header">
                    <span className="section-icon">🚨</span>
                    <div>
                      <h3>Critical Leaks</h3>
                      <p>Costing you the most ELO right now at {report.meta.playerElo}</p>
                    </div>
                  </div>
                  {report.criticalLeaks.map((w, i) => (
                    <WeaknessCard key={w.tag} weakness={w} rank={i+1} type="critical"
                      games={report.games} playerElo={elo}
                      isExpanded={expandedWeakness === w.tag}
                      onToggle={() => setExpandedWeakness(expandedWeakness === w.tag ? null : w.tag)}
                      onAITag={(newLabel) => { w.label = newLabel; }} />
                  ))}
                </div>
              )}

              {report.growthMilestones.length > 0 && (
                <div className="weakness-section" style={{ marginTop: 28 }}>
                  <div className="section-header">
                    <span className="section-icon">🚀</span>
                    <div>
                      <h3>Growth Milestones</h3>
                      <p>Master these to reach {report.meta.targetElo} ELO</p>
                    </div>
                  </div>
                  {report.growthMilestones.map((w, i) => (
                    <WeaknessCard key={w.tag} weakness={w} rank={i+1} type="growth"
                      games={report.games} playerElo={elo}
                      isExpanded={expandedWeakness === w.tag}
                      onToggle={() => setExpandedWeakness(expandedWeakness === w.tag ? null : w.tag)}
                      onAITag={(newLabel) => { w.label = newLabel; }} />
                  ))}
                </div>
              )}

              {!report.criticalLeaks.length && !report.growthMilestones.length && (
                <div className="no-issues">
                  <div>🔍</div>
                  <h3>No significant issues found</h3>
                  <p>Try uploading more games or check the browser console (F12) for API errors.</p>
                </div>
              )}
            </div>

            <div className="results-right">
              <TimeManagementPanel
                tm={report.timeManagement}
                playerElo={elo}
                coaching={tmCoaching}
                setCoaching={setTmCoaching}
              />
              <CurriculumPanel
                curriculum={curriculum}
                loading={curriculumLoading}
                playerElo={elo}
                targetElo={elo + 200}
                timeManagement={report.timeManagement}
                tmCoaching={tmCoaching}
                username={report.meta?.username}
                onRegenerate={async () => {
                  setCurriculumLoading(true);
                  setCurriculum(null);
                  try {
                    const curr = await generateCurriculum(
                      [...(report.criticalLeaks || []), ...(report.growthMilestones || [])],
                      elo
                    );
                    setCurriculum(curr && curr.length > 0 ? curr : null);
                  } catch(e) { console.error(e); }
                  finally { setCurriculumLoading(false); }
                }}
              />
              <div className="games-panel">
                <h3>🎮 Game Breakdown</h3>
                <div className="game-tabs">
                  {report.games.map((g, i) => (
                    <button key={i} className={`game-tab ${activeGame === i ? "active" : ""}`} onClick={() => setActiveGame(i)}>
                      Game {i+1}
                    </button>
                  ))}
                </div>
                {report.games[activeGame] && (
                  <div className="game-detail">
                    <div className="game-players">
                      <span>♔ {report.games[activeGame].white}</span>
                      <span className="vs">vs</span>
                      <span>♚ {report.games[activeGame].black}</span>
                    </div>
                    <div className="game-result">{report.games[activeGame].result}</div>
                    <div className="game-opening">{report.games[activeGame].opening}</div>
                    <div className="game-mistakes">
                      {report.tagged.filter(t => t.gameIndex === activeGame).length} issues found
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
